import React, { useEffect, useState, useRef, forwardRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardCheck,
  Box,
  Upload,
  Copy,
  Download,
  History,
  MessageSquare,
  XCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import commonStyles from '../../styles/common.module.css';
import styles from './RepairRequestDetails.module.css';
import { useAuth } from '../../context/AuthContext';
import EquipmentHistoryPanel from '../EquipmentHistory/EquipmentHistoryPanel';
import CreateClientTicketPanel from '../ClientTickets/CreateClientTicketPanel';
import Loading from '../Loading';
import { shouldSendToTelegram } from '../../utils/telegram';
import AsyncBasketSelect from '../Shared/AsyncBasketSelect';
import EquipmentSummary from './EquipmentSummary';
const TASK_POLL_INTERVAL = 3000;
const TASK_POLL_TIMEOUT = 5 * 60 * 1000;

const RepairRequestDetails = forwardRef(({ repairRequestId: propRepairRequestId }, ref) => {
  const { id: paramId } = useParams();
  const repairRequestId = propRepairRequestId || paramId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPanel = !!propRepairRequestId;

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scannedSerial, setScannedSerial] = useState('');
  const [scannedList, setScannedList] = useState([]);
  const [selectedBasket, setSelectedBasket] = useState(null);
  const [isSticky, setIsSticky] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [isExcelDragActive, setIsExcelDragActive] = useState(false);
  const excelInputRef = useRef(null);
  const excelDragCounterRef = useRef(0);
  const [invalidEquipments, setInvalidEquipments] = useState([]);
  const [equipmentItems, setEquipmentItems] = useState([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [equipmentTotal, setEquipmentTotal] = useState(0);
  const [equipmentPage, setEquipmentPage] = useState(1);
  const [equipmentHasMore, setEquipmentHasMore] = useState(false);
  const serialInputRef = useRef(null);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  const [isTicketPanelOpen, setIsTicketPanelOpen] = useState(false);
  const [missingSelectedIds, setMissingSelectedIds] = useState([]);
  const [missingReason, setMissingReason] = useState('');
  const [isMarkingMissing, setIsMarkingMissing] = useState(false);

  const pollTaskStatus = useCallback(async (taskId) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < TASK_POLL_TIMEOUT) {
      try {
        const { data } = await api.get(`/repairs/tasks/${taskId}/`);
        const status = data?.status;
        if (status === 'SUCCESS') {
          return data.result || {};
        }
        if (status === 'FAILURE' || status === 'REVOKED') {
          const detail = data?.error || 'Задача завершилась с ошибкой';
          throw new Error(detail);
        }
      } catch (err) {
        throw err;
      }
      await new Promise(resolve => setTimeout(resolve, TASK_POLL_INTERVAL));
    }
    throw new Error('Превышено время ожидания обработки файла');
  }, []);

  // Проверяем роль пользователя
  const roleNames = (user?.roles || []).map(role => role.name);
  const canReceive = roleNames.includes('admin') || roleNames.includes('приёмщик');

  // Проверяем, является ли заявка высланной
  const isShippedRequest = request?.status === 'shipped';
  const currentRequestId = request?.id;
  const currentRequestNumber = request?.request_number;

  const fetchRequest = useCallback(async () => {
    let success = false;
    setLoading(true);
    setEquipmentLoading(true);
    const requestPromise = api.get(`/repairs/repair-requests/${repairRequestId}/`, {
      params: { with_details: false },
    });
    const equipmentPromise = api.get(`/repairs/repair-requests/${repairRequestId}/equipment_summary/`, {
      params: { page: 1, page_size: 100 },
    });
    try {
      const { data } = await requestPromise;
      setRequest(data);
      setError('');
      success = true;
      setLoading(false);

      try {
        const { data: equipmentData } = await equipmentPromise;
        const equipments = Array.isArray(equipmentData?.results) ? equipmentData.results : [];
        setEquipmentItems(equipments);
        setEquipmentTotal(equipmentData?.count ?? data?.total_equipment ?? equipments.length);
        setEquipmentPage(1);
        setEquipmentHasMore(Boolean(equipmentData?.next));
        setMissingSelectedIds(prev => prev.filter(id => equipments.some(item => item.id === id)));
      } catch (equipmentError) {
        console.error('Ошибка при загрузке оборудования заявки:', equipmentError);
        setEquipmentItems([]);
        setEquipmentTotal(data?.total_equipment || 0);
        setEquipmentHasMore(false);
        toast.error('Не удалось загрузить список оборудования', {
          toastId: 'fetch-request-equipment-error',
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error('Ошибка при загрузке деталей заявки:', err);
      const errorMsg = err.response?.data?.error || 'Ошибка при загрузке деталей заявки';
      setError(errorMsg);
      setEquipmentItems([]);
      setEquipmentTotal(0);
      setEquipmentHasMore(false);
      toast.error(errorMsg, { toastId: 'fetch-request-error', autoClose: 3000 });
      return false;
    } finally {
      setLoading(false);
      setEquipmentLoading(false);
    }
    return success;
  }, [repairRequestId]);

  const loadMoreEquipment = async () => {
    if (equipmentLoading || !equipmentHasMore) return;
    const nextPage = equipmentPage + 1;
    try {
      setEquipmentLoading(true);
      const { data } = await api.get(`/repairs/repair-requests/${repairRequestId}/equipment_summary/`, {
        params: { page: nextPage, page_size: 100 },
      });
      const newItems = Array.isArray(data?.results) ? data.results : [];
      setEquipmentItems(prev => [
        ...prev,
        ...newItems.filter(item => !prev.some(existing => existing.id === item.id)),
      ]);
      setEquipmentTotal(data?.count ?? equipmentTotal);
      setEquipmentPage(nextPage);
      setEquipmentHasMore(Boolean(data?.next));
    } catch (err) {
      console.error('Ошибка при загрузке следующей страницы оборудования:', err);
      toast.error('Не удалось загрузить следующую часть списка');
    } finally {
      setEquipmentLoading(false);
    }
  };

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  useEffect(() => {
    if (isPanel) {
      return undefined;
    }
    const handleScroll = () => {
      setIsSticky(window.scrollY > 350);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isPanel]);

  useEffect(() => {
    if (serialInputRef.current) {
      serialInputRef.current.focus();
    }
  }, []);

  const handleBasketSelect = (selectedOption) => {
    setSelectedBasket(selectedOption);
  };

  const handleSerialChange = (e) => {
    const serialRaw = e.target.value.trim();
    const serial = serialRaw.toLowerCase();
    setScannedSerial(serial);

    if (!serial || !request) return;

    if (equipmentLoading) {
      return;
    }

    const foundEq = equipmentItems.find(eq => eq.serial_number?.trim().toLowerCase() === serial);

    if (foundEq) {
      if (foundEq.location_status !== 'awaiting_receipt') {
        toast.error('Оборудование уже принято или находится в обработке', {
          toastId: 'add-serial-already-accepted',
          autoClose: 3000,
        });
        setScannedSerial('');
        return;
      }
      if (foundEq.location_status === 'shipped') {
        toast.error('Оборудование отправлено и не может быть оприходовано', {
          toastId: 'add-serial-shipped',
          autoClose: 3000,
        });
        setScannedSerial('');
        return;
      }
      if (scannedList.some(eq => eq.id === foundEq.id)) {
        toast.error('Этот серийный номер уже добавлен для оприходования', {
          toastId: 'add-serial-duplicate',
          autoClose: 3000,
        });
        setScannedSerial('');
        return;
      }

      setScannedList(prev => [...prev, foundEq]);
      setScannedSerial('');
      toast.success(`Серийный номер ${serialRaw} добавлен для оприходования`, {
        toastId: `add-serial-${foundEq.id}`,
        autoClose: 3000,
      });
    }
  };

  const handleSerialKeyDown = async (event) => {
    if (event.key !== 'Enter' || !scannedSerial.trim() || equipmentLoading) return;
    event.preventDefault();
    const serialRaw = scannedSerial.trim();
    if (equipmentItems.some(eq => eq.serial_number?.trim().toLowerCase() === serialRaw.toLowerCase())) {
      return;
    }

    try {
      setEquipmentLoading(true);
      const { data } = await api.get(`/repairs/repair-requests/${repairRequestId}/equipment_summary/`, {
        params: { search: serialRaw, exact: true, page_size: 2 },
      });
      const foundEq = data?.results?.[0];
      if (!foundEq) {
        toast.error(`Серийный номер «${serialRaw}» не найден в заявке ${currentRequestNumber || ''}. Проверьте номер и заполнение колонок «Серийный номер» и «Модель оборудования» в исходном файле.`, {
          toastId: 'receipt-serial-not-found', autoClose: 8000,
        });
        return;
      }
      if (foundEq.location_status !== 'awaiting_receipt') {
        toast.error('Оборудование уже принято или находится в обработке');
        setScannedSerial('');
        return;
      }
      if (scannedList.some(eq => eq.id === foundEq.id)) {
        toast.error('Этот серийный номер уже добавлен для оприходования');
        setScannedSerial('');
        return;
      }
      setScannedList(prev => [...prev, foundEq]);
      setScannedSerial('');
      toast.success(`Серийный номер ${serialRaw} добавлен для оприходования`);
    } catch (err) {
      console.error('Ошибка поиска оборудования в заявке:', err);
      const status = err.response?.status;
      const message = status === 404
        ? 'Заявка не найдена или у вас нет доступа к ней. Обновите список заявок.'
        : status === 403
          ? 'Нет разрешения на просмотр оборудования этой заявки.'
          : 'Не удалось связаться с сервером для проверки серийного номера. Повторите поиск.';
      toast.error(message, { toastId: 'receipt-serial-check-error', autoClose: 6000 });
    } finally {
      setEquipmentLoading(false);
    }
  };

  const resetExcelDragState = () => {
    excelDragCounterRef.current = 0;
    setIsExcelDragActive(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const isExcel = file && (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls'));
    if (isExcel) {
      setExcelFile(file);
    } else if (file) {
      toast.error('Пожалуйста, выберите файл Excel (.xls или .xlsx)', {
        toastId: 'invalid-file-type',
        autoClose: 3000,
      });
      setExcelFile(null);
    }
    resetExcelDragState();
  };

  const handleExcelDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleExcelDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    excelDragCounterRef.current += 1;
    if (!isExcelDragActive) {
      setIsExcelDragActive(true);
    }
  };

  const handleExcelDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    excelDragCounterRef.current = Math.max(excelDragCounterRef.current - 1, 0);
    if (excelDragCounterRef.current === 0) {
      setIsExcelDragActive(false);
    }
  };

  const handleExcelDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const droppedFile = event.dataTransfer?.files?.[0];
    if (droppedFile) {
      const isExcel = droppedFile.name.toLowerCase().endsWith('.xlsx') || droppedFile.name.toLowerCase().endsWith('.xls');
      if (isExcel) {
        setExcelFile(droppedFile);
      } else {
        toast.error('Пожалуйста, выберите файл Excel (.xls или .xlsx)', {
          toastId: 'invalid-file-type-drop',
          autoClose: 3000,
        });
        setExcelFile(null);
      }
    }
    resetExcelDragState();
  };

  const handleExcelSelectClick = () => {
    excelInputRef.current?.click();
  };

  const handleUploadExcel = async () => {
    if (!excelFile) {
      toast.error('Выберите файл для загрузки', { toastId: 'no-file-selected', autoClose: 3000 });
      return;
    }

    const formData = new FormData();
    formData.append('file', excelFile);

    try {
      setLoading(true);
      const { data } = await api.post(`/repairs/repair-requests/${repairRequestId}/receive_excel/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (data?.task_id) {
        toast.info('Файл принят, начинается обработка', {
          toastId: `receive-excel-start-${repairRequestId}`,
          autoClose: 2000,
        });
        const result = await pollTaskStatus(data.task_id);
        setInvalidEquipments(result?.invalid_equipments || []);
        const successMessage = result?.detail || 'Оборудование успешно обработано';
        toast.success(successMessage, {
          toastId: `receive-excel-success-${repairRequestId}`,
          autoClose: 3000,
        });
      } else {
        setInvalidEquipments(data?.invalid_equipments || []);
        const message = data?.detail || 'Оборудование успешно обработано';
        toast.success(message, {
          toastId: `receive-excel-success-${repairRequestId}`,
          autoClose: 3000,
        });
      }

      setScannedList([]);
      setExcelFile(null);
      await fetchRequest();
    } catch (err) {
      console.error('Ошибка при загрузке файла:', err);
      const errorData = err.response?.data;
      if (errorData?.invalid_equipments) {
        setInvalidEquipments(errorData.invalid_equipments);
        toast.warn('Некоторые серийные номера не найдены', {
          toastId: 'receive-excel-warning',
          autoClose: 5000,
        });
      } else if (errorData?.error) {
        setError(errorData.error);
        toast.error(errorData.error, { toastId: 'receive-excel-error', autoClose: 3000 });
      } else {
        toast.error(err.message || 'Ошибка при загрузке файла', {
          toastId: 'receive-excel-error',
          autoClose: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = useCallback(async () => {
    if (!currentRequestId) {
      toast.error('Заявка не найдена', {
        toastId: 'template-no-request',
        autoClose: 3000,
      });
      return;
    }
    try {
      if (shouldSendToTelegram()) {
        try {
          await api.get(`/repairs/repair-requests/${currentRequestId}/excel_template/`, {
            params: { send_to_telegram: true },
          });
          toast.success('Шаблон отправлен в Telegram', {
            toastId: 'download-template-success',
            autoClose: 2000,
          });
          return;
        } catch (err) {
          console.warn('Telegram send failed, fallback to download:', err);
        }
      }

      const response = await api.get(`/repairs/repair-requests/${currentRequestId}/excel_template/`, {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const filename = `repair_request_${currentRequestNumber || currentRequestId}_template.xlsx`;
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Шаблон успешно скачан', {
        toastId: 'download-template-success',
        autoClose: 2000,
      });
    } catch (err) {
      console.error('Ошибка при скачивании шаблона:', err);
      toast.error('Не удалось скачать шаблон', {
        toastId: 'download-template-error',
        autoClose: 3000,
      });
    }
  }, [currentRequestId, currentRequestNumber]);

  const handleCopyInvalid = () => {
    const text = invalidEquipments
      .map(item => `${item.serial_number}\t${item.basket || 'Не указана'}`)
      .join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Список скопирован в буфер обмена', {
      toastId: 'copy-invalid-success',
      autoClose: 3000,
    });
  };

  const handleDownloadInvalid = () => {
    const headers = ['Серийный номер', 'Корзина'];
    const rows = invalidEquipments.map(item => [
      item.serial_number,
      item.basket || 'Не указана',
    ]);
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'invalid_equipments.csv';
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Файл загружен', { toastId: 'download-invalid-success', autoClose: 3000 });
  };

  const handleHistoryClick = async (equipment) => {
    setSelectedEquipmentId(equipment.id);
    setHistoryPanelOpen(true);
  };

  const closeHistoryPanel = () => {
    setHistoryPanelOpen(false);
    setSelectedEquipmentId(null);
  };

  const toggleMissingSelection = (equipmentId) => {
    setMissingSelectedIds(prev => (
      prev.includes(equipmentId)
        ? prev.filter(id => id !== equipmentId)
        : [...prev, equipmentId]
    ));
  };

  const handleMarkMissing = async () => {
    if (missingSelectedIds.length === 0) {
      toast.error('Выберите оборудование, которое не поступило', {
        toastId: 'missing-no-equipment',
        autoClose: 3000,
      });
      return;
    }
    if (!missingReason.trim()) {
      toast.error('Укажите причину', {
        toastId: 'missing-no-reason',
        autoClose: 3000,
      });
      return;
    }
    const confirmed = window.confirm(`Отметить как не поступившие: ${missingSelectedIds.length} шт.?`);
    if (!confirmed) return;
    try {
      setIsMarkingMissing(true);
      const { data } = await api.post(`/repairs/client-requests/${repairRequestId}/mark-not-received/`, {
        equipment_ids: missingSelectedIds,
        reason: missingReason.trim(),
      });
      setRequest(data);
      const equipments = Array.isArray(data?.equipment_items) ? data.equipment_items : [];
      setEquipmentItems(equipments);
      setEquipmentTotal(data?.total_equipment ?? equipments.length ?? 0);
      setMissingSelectedIds([]);
      setMissingReason('');
      toast.success('Оборудование отмечено как не поступившее', {
        toastId: 'missing-success',
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Ошибка отметки непоступившего оборудования:', err);
      toast.error(err.response?.data?.error || 'Не удалось отметить оборудование', {
        toastId: 'missing-error',
        autoClose: 3000,
      });
    } finally {
      setIsMarkingMissing(false);
    }
  };

  const handleAccept = async () => {
    if (scannedList.length === 0) {
      toast.error('Добавьте хотя бы один серийный номер', {
        toastId: 'accept-no-serial',
        autoClose: 3000,
      });
      return;
    }
    if (!selectedBasket) {
      toast.error('Пожалуйста, выберите корзину', {
        toastId: 'accept-no-basket',
        autoClose: 3000,
      });
      return;
    }
    try {
      setLoading(true);
      await Promise.all(
        scannedList.map(eq =>
          api.post(`/repairs/equipments/${eq.id}/update_status/`, {
            new_status: 'awaiting_diagnosis',
            basket_id: selectedBasket.value,
          })
        )
      );
      await fetchRequest();
      setScannedList([]);
      toast.success('Оборудование успешно оприходовано', {
        toastId: 'accept-success',
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Ошибка при оприходовании:', err);
      const errorMsg = err.response?.data?.error;
      if (errorMsg) {
        setError(errorMsg);
        toast.error(errorMsg, { toastId: 'accept-error', autoClose: 3000 });
      } else {
        toast.error('Не удалось оприходовать оборудование', {
          toastId: 'accept-error',
          autoClose: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minWidth: '300px',
    }),
    option: (provided) => ({
      ...provided,
      color: '#000',
    }),
    menu: (provided) => ({
      ...provided,
      width: '300px',
      zIndex: 99999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 99999,
    }),
  };

  if (loading) return <div className={commonStyles.loading}><div className={commonStyles.spinner}></div></div>;
  if (loading) return <Loading />;

  return (
    <div className={`${commonStyles.pageContainer} ${commonStyles.fadeIn}`}>
      {isSticky && request && !isShippedRequest && canReceive && !isPanel && (
        <div className={commonStyles.stickyHeader}>
          <div className={commonStyles.stickyInfo}>
            <ClipboardCheck className={styles.statusIcon} />
            <span className={commonStyles.stickyTitle}>
              Заявка: {request.request_number}
            </span>
            <span className={commonStyles.stickyStatus}>
              {request.status_display}
            </span>
          </div>
          <div className={commonStyles.stickyControls}>
            <AsyncBasketSelect
              repairRequestId={repairRequestId}
              targetStatus="awaiting_diagnosis"
              value={selectedBasket}
              onChange={handleBasketSelect}
              className={styles.selectComponent}
              classNamePrefix="select"
              styles={customSelectStyles}
              isDisabled={loading || !request}
            />
            <button
              className={`${commonStyles.button} ${styles.controlButton}`}
              onClick={handleAccept}
              disabled={loading || !request}
            >
              <Box size={18} />
              Оприходовать
            </button>
          </div>
        </div>
      )}

      <div className={styles.pageHeader}>
        {!isPanel && (
          <button className={styles.backButton} onClick={() => navigate('/repair-requests')}>
            <ArrowLeft size={20} />
            Назад к списку
          </button>
        )}
        {request && (
          <div className={styles.headerContent}>
            <div className={styles.titleGroup}>
              <ClipboardCheck className={styles.titleIcon} />
              <h1 className={commonStyles.pageTitle}>
                {isShippedRequest ? 'Высланная заявка' : 'Детали заявки'}: {request.request_number}
              </h1>
              <button
                type="button"
                className={styles.ticketButton}
                onClick={() => setIsTicketPanelOpen(true)}
              >
                <MessageSquare size={17} />
                Тикет
              </button>
            </div>
            <div className={styles.equipmentInfo}>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Заказчик</span>
                  <span className={styles.infoValue}>{request.customer.name}</span>
                </div>
                {request.territory ? (
                  <>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Территория</span>
                      <span className={styles.infoValue}>
                        {request.territory.tb_name || request.territory.gosb_name || 'Не указана'}
                      </span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Город</span>
                      <span className={styles.infoValue}>{request.territory.city.name}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Адрес</span>
                      <span className={styles.infoValue}>{request.territory.address || 'Не указан'}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Сервисный центр</span>
                      <span className={styles.infoValue}>{request.service_center.name}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Город</span>
                      <span className={styles.infoValue}>{request.service_center.city.name}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Адрес</span>
                      <span className={styles.infoValue}>
                        {request.service_center.address || 'Не указан'}
                      </span>
                    </div>
                  </>
                )}
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Количество оборудования</span>
                  <span className={styles.infoValue}>{request.total_equipment}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Статус</span>
                  <span className={styles.infoValue}>{request.status_display}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.mainContent}>
        <div className={styles.leftColumn}>
          {!isShippedRequest && canReceive && (
            <div className={styles.addSerialSection}>
              <h3 className={styles.sectionTitle}>
                Добавить серийный номер для оприходования
              </h3>
              <div className={styles.inputRow}>
                <input
                  type="text"
                  placeholder="Введите серийный номер"
                  value={scannedSerial}
                  onChange={handleSerialChange}
                  onKeyDown={handleSerialKeyDown}
                  className={styles.serialInput}
                  disabled={loading || !request}
                  ref={serialInputRef}
                />
              </div>
              <div className={styles.uploadSection}>
                <h4>Загрузить список из Excel</h4>
              <div className={styles.templateActions}>
                <button
                  type="button"
                  className={`${styles.uploadButton} ${styles.templateButton}`}
                  onClick={handleDownloadTemplate}
                  disabled={loading || !currentRequestId}
                >
                  <Download size={18} />
                  Скачать шаблон
                </button>
                <p className={styles.templateHint}>
                  Обязательные колонки: «Серийный номер» и «Корзина».
                </p>
              </div>
              <div
                className={`${styles.fileDropzone} ${isExcelDragActive ? styles.fileDropzoneActive : ''}`}
                onDragEnter={handleExcelDragEnter}
                onDragLeave={handleExcelDragLeave}
                onDragOver={handleExcelDragOver}
                  onDrop={handleExcelDrop}
                >
                  <input
                    ref={excelInputRef}
                    type="file"
                    accept=".xls,.xlsx"
                    onChange={handleFileChange}
                    className={styles.hiddenFileInput}
                    disabled={loading || !request}
                  />
                  <div
                    role="button"
                    tabIndex={0}
                    className={styles.fileDropContent}
                    onClick={handleExcelSelectClick}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleExcelSelectClick();
                      }
                    }}
                  >
                    <p className={styles.fileDropTitle}>
                      {excelFile ? excelFile.name : 'Перетащите файл сюда'}
                    </p>
                    <p className={styles.fileDropSubtitle}>
                      {excelFile
                        ? 'Перетащите новый файл или нажмите, чтобы заменить'
                        : 'или нажмите, чтобы выбрать файл в формате .xls/.xlsx'}
                    </p>
                  </div>
                  <div className={styles.fileActions}>
                    <button
                      type="button"
                      className={styles.fileSelectButton}
                      onClick={handleExcelSelectClick}
                      disabled={loading || !request}
                    >
                      Выбрать файл
                    </button>
                    {excelFile && (
                      <button
                        type="button"
                        className={styles.clearButton}
                        onClick={() => setExcelFile(null)}
                      >
                        Очистить
                      </button>
                    )}
                  </div>
                </div>
                <button
                  className={styles.uploadButton}
                  onClick={handleUploadExcel}
                  disabled={loading || !excelFile}
                >
                  <Upload size={18} />
                  Загрузить
                </button>
              </div>
            </div>
          )}

          {!isShippedRequest && canReceive && invalidEquipments.length > 0 && (
            <div className={styles.invalidEquipmentSection}>
              <h4 className={styles.sectionTitle}>Лишнее оборудование из файла:</h4>
              <div className={styles.invalidControls}>
                <button
                  className={styles.controlButton}
                  onClick={handleCopyInvalid}
                >
                  <Copy size={18} />
                  Копировать
                </button>
                <button
                  className={styles.controlButton}
                  onClick={handleDownloadInvalid}
                >
                  <Download size={18} />
                  Скачать CSV
                </button>
              </div>
              <ul className={commonStyles.list}>
                {invalidEquipments.map((item, index) => (
                  <li key={index} className={styles.listItem}>
                    {item.serial_number} — Корзина: {item.basket || 'Не указана'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!isShippedRequest && canReceive && scannedList.length > 0 && (
            <div className={styles.scannedList}>
              <h4 className={styles.sectionTitle}>Оборудование на оприходование:</h4>
              <ul className={commonStyles.list}>
                {scannedList.map(eq => (
                  <li key={eq.id} className={styles.listItem}>
                    <EquipmentSummary equipment={eq} showStatus={false} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {request && (
            <div className={styles.equipmentSection}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Оборудование заявки</h3>
                <span className={styles.sectionSubtitle}>
                  Показано {equipmentItems.length} из {equipmentTotal || request.total_equipment}
                </span>
              </div>
              {!isShippedRequest && canReceive && (
                <div className={styles.missingPanel}>
                  <div>
                    <strong>Не поступившее оборудование</strong>
                    <span>Галочки ниже отмечают только оборудование, которое не поступило. Для приёмки используйте поле сканирования.</span>
                  </div>
                  <textarea
                    value={missingReason}
                    onChange={(event) => setMissingReason(event.target.value)}
                    placeholder="Причина: не было в коробке, клиент не передал, повреждена упаковка..."
                    rows={2}
                  />
                  <button
                    type="button"
                    className={styles.missingButton}
                    onClick={handleMarkMissing}
                    disabled={isMarkingMissing || missingSelectedIds.length === 0}
                  >
                    <XCircle size={17} />
                    {isMarkingMissing ? 'Сохраняем...' : `Не поступило (${missingSelectedIds.length})`}
                  </button>
                </div>
              )}
              <div className={styles.equipmentScrollArea}>
                {equipmentLoading && !equipmentItems.length ? (
                  <div className={styles.equipmentLoading}>Загрузка...</div>
                ) : (
                  <ul className={styles.equipmentList}>
                    {equipmentItems.map(eq => (
                      <li key={eq.id} className={styles.listItem}>
                        <div className={styles.equipmentItemContent}>
                          {!isShippedRequest && canReceive && eq.location_status === 'awaiting_receipt' && (
                            <input
                              type="checkbox"
                              className={styles.missingCheckbox}
                              checked={missingSelectedIds.includes(eq.id)}
                              onChange={() => toggleMissingSelection(eq.id)}
                              title="Отметить как не поступившее"
                              aria-label={`Не поступило: ${eq.serial_number}`}
                            />
                          )}
                          <EquipmentSummary equipment={eq} />
                          <button
                            className={styles.historyButton}
                            onClick={() => handleHistoryClick(eq)}
                            title="Показать историю оборудования"
                            aria-label={`История оборудования ${eq.serial_number}`}
                          >
                            <History size={16} />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {equipmentLoading && equipmentItems.length > 0 && (
                <div className={styles.equipmentLoading}>Обновление списка...</div>
              )}
              {!equipmentLoading && equipmentHasMore && (
                <button type="button" className={styles.loadMoreButton} onClick={loadMoreEquipment}>
                  Показать ещё 100
                </button>
              )}
              {!equipmentLoading && !equipmentItems.length && (
                <div className={styles.emptyState}>Оборудование не найдено</div>
              )}
            </div>
          )}
        </div>

        {!isShippedRequest && canReceive && (
          <div className={styles.rightColumn}>
            <div className={styles.basketSection}>
              <h3 className={styles.sectionTitle}>Выбор корзины</h3>
              <AsyncBasketSelect
                repairRequestId={repairRequestId}
                targetStatus="awaiting_diagnosis"
                value={selectedBasket}
                onChange={handleBasketSelect}
                className={styles.selectComponent}
                classNamePrefix="select"
                styles={customSelectStyles}
                isDisabled={loading || !request}
                menuPortalTarget={document.body}
              />
            </div>

            <div className={styles.actionButtons}>
              <button
                className={`${commonStyles.button} ${styles.controlButton}`}
                onClick={handleAccept}
                disabled={loading || !request}
              >
                <Box size={18} />
                Оприходовать
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Equipment History Panel */}
      <EquipmentHistoryPanel
        isOpen={historyPanelOpen}
        onClose={closeHistoryPanel}
        equipmentId={selectedEquipmentId}
        title="История оборудования"
      />
      <CreateClientTicketPanel
        isOpen={isTicketPanelOpen}
        repairRequest={request}
        onClose={() => setIsTicketPanelOpen(false)}
      />
    </div>
  );
});

RepairRequestDetails.displayName = 'RepairRequestDetails';

export default RepairRequestDetails;
